import React, { useState, useEffect, useCallback } from 'react';
import { Calculator, AlertTriangle, DollarSign, Car, Calendar, Percent, LogIn, LogOut, Save, Package, Wrench, Shield, CheckCircle2, AlertCircle, FileText, Menu, X as XIcon, History, ArrowLeft, Wifi, WifiOff, ThumbsUp, ThumbsDown, Lightbulb } from 'lucide-react';
import { calculatePayments, analyzeTerms, type LeaseAnalysis, moneyFactorToAPR, generateGoodLeaseTerms } from './utils/leaseCalculator';
import { auth } from './lib/firebase';
import { User } from 'firebase/auth';
import { AuthModal } from './components/AuthModal';
import { LeaseHistory } from './components/LeaseHistory';
import { ComparisonModal } from './components/ComparisonModal';
import { saveLease } from './services/leaseService';
import type { LeaseRecord } from './types/lease';
import { validateLeaseData, type ValidationErrors } from './utils/validation';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';
import { FormFields } from './components/FormFields';

function App() {
  const initialLeaseData = {
    msrp: '45000',  // Example of a mid-range luxury vehicle
    sellingPrice: '41400',  // 8% below MSRP (good negotiation)
    residualValue: '27000',  // 60% residual value (strong)
    moneyFactor: '0.00200',  // 4.8% APR (competitive rate)
    term: '36',  // Standard 3-year lease
    downPayment: '2000',  // Low down payment (good for lease)
    taxes: '0',  // Will be included in monthly payment
    fees: '895',  // Reasonable acquisition fee
    dealerAddOns: {
      protectionPackage: '0',
      serviceContract: '0',
      dealerInstalledEquipment: '0',
      predeliveryCharge: '0'
    },
    acquisitionFee: '895'
  };

  const [leaseData, setLeaseData] = useState(initialLeaseData);
  const [analysis, setAnalysis] = useState<LeaseAnalysis | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [comparisonLease, setComparisonLease] = useState<{ data: any; analysis: LeaseAnalysis } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [activeTab, setActiveTab] = useState<'input' | 'analysis' | 'history'>('input');
  const [isLogin, setIsLogin] = useState(true);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((newUser) => {
      setUser(newUser);
      if (!newUser) {
        setLeaseData(initialLeaseData);
        setAnalysis(null);
        setShowComparisonModal(false);
        setComparisonLease(null);
        setSaving(false);
        setSaveError('');
        setActiveTab('input');
        setValidationErrors({});
      }
    });
    return () => unsubscribe();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setLeaseData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev],
          [child]: value
        }
      }));
    } else {
      setLeaseData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  }, []);

  const handleGreenThumb = useCallback(() => {
    const currentMsrp = parseFloat(leaseData.msrp);
    if (!currentMsrp || isNaN(currentMsrp)) {
      alert('Please enter a valid MSRP first');
      return;
    }

    const goodTerms = generateGoodLeaseTerms(currentMsrp);
    setLeaseData({
      msrp: goodTerms.msrp.toString(),
      sellingPrice: goodTerms.sellingPrice.toString(),
      residualValue: goodTerms.residualValue.toString(),
      moneyFactor: goodTerms.moneyFactor.toString(),
      term: goodTerms.term.toString(),
      downPayment: goodTerms.downPayment.toString(),
      taxes: goodTerms.taxes.toString(),
      fees: goodTerms.fees.toString(),
      dealerAddOns: {
        protectionPackage: '0',
        serviceContract: '0',
        dealerInstalledEquipment: '0',
        predeliveryCharge: '0'
      },
      acquisitionFee: goodTerms.fees.toString()
    });
  }, [leaseData.msrp]);

  const handleAnalyze = useCallback(() => {
    const errors = validateLeaseData(leaseData);
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }

    const totalFees = 
      parseFloat(leaseData.fees) || 
      ((parseFloat(leaseData.dealerAddOns.protectionPackage) || 0) +
      (parseFloat(leaseData.dealerAddOns.serviceContract) || 0) +
      (parseFloat(leaseData.dealerAddOns.dealerInstalledEquipment) || 0) +
      (parseFloat(leaseData.dealerAddOns.predeliveryCharge) || 0) +
      (parseFloat(leaseData.acquisitionFee) || 0));

    const parsedData = {
      msrp: parseFloat(leaseData.msrp) || 0,
      sellingPrice: parseFloat(leaseData.sellingPrice) || 0,
      residualValue: parseFloat(leaseData.residualValue) || 0,
      moneyFactor: parseFloat(leaseData.moneyFactor) || 0,
      term: parseInt(leaseData.term) || 36,
      downPayment: parseFloat(leaseData.downPayment) || 0,
      taxes: parseFloat(leaseData.taxes) || 0,
      fees: totalFees || 0
    };

    if (parsedData.sellingPrice && parsedData.residualValue && parsedData.moneyFactor) {
      const result = analyzeTerms(parsedData);
      setAnalysis(result);
      setActiveTab('analysis');
    }
  }, [leaseData]);

  const handleSaveAnalysis = useCallback(async () => {
    if (!user || !analysis) return;
    
    setSaving(true);
    setSaveError('');
    
    try {
      const totalFees = 
        parseFloat(leaseData.fees) || 
        ((parseFloat(leaseData.dealerAddOns.protectionPackage) || 0) +
        (parseFloat(leaseData.dealerAddOns.serviceContract) || 0) +
        (parseFloat(leaseData.dealerAddOns.dealerInstalledEquipment) || 0) +
        (parseFloat(leaseData.dealerAddOns.predeliveryCharge) || 0) +
        (parseFloat(leaseData.acquisitionFee) || 0));

      const parsedData = {
        msrp: parseFloat(leaseData.msrp) || 0,
        sellingPrice: parseFloat(leaseData.sellingPrice) || 0,
        residualValue: parseFloat(leaseData.residualValue) || 0,
        moneyFactor: parseFloat(leaseData.moneyFactor) || 0,
        term: parseInt(leaseData.term) || 36,
        downPayment: parseFloat(leaseData.downPayment) || 0,
        taxes: parseFloat(leaseData.taxes) || 0,
        fees: totalFees || 0
      };
      
      await saveLease(parsedData, user.uid);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save analysis');
    } finally {
      setSaving(false);
    }
  }, [user, analysis, leaseData]);

  const handleSelectLease = useCallback((lease: LeaseRecord) => {
    if (analysis) {
      setComparisonLease({
        data: leaseData,
        analysis: analysis
      });
      setShowComparisonModal(true);
    }
    
    setLeaseData({
      msrp: lease.msrp.toString(),
      sellingPrice: lease.sellingPrice.toString(),
      residualValue: lease.residualValue.toString(),
      moneyFactor: lease.moneyFactor.toString(),
      term: lease.term.toString(),
      downPayment: lease.downPayment.toString(),
      taxes: lease.taxes.toString(),
      fees: lease.fees.toString(),
      dealerAddOns: {
        protectionPackage: '',
        serviceContract: '',
        dealerInstalledEquipment: '',
        predeliveryCharge: '',
      },
      acquisitionFee: '',
    });
    
    handleAnalyze();
    setActiveTab('input');
  }, [analysis, leaseData, handleAnalyze]);

  return (
    <div className="min-h-screen bg-gray-50">
      {isOffline && (
        <div className="bg-yellow-50 p-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
            <div className="flex items-center">
              <WifiOff className="h-5 w-5 text-yellow-400 mr-2" />
              <p className="text-sm text-yellow-700">
                You're currently offline. Some features may be limited.
              </p>
            </div>
            {navigator.onLine && (
              <button
                onClick={() => window.location.reload()}
                className="text-sm text-yellow-700 hover:text-yellow-600 font-medium"
              >
                <Wifi className="h-4 w-4 inline mr-1" />
                Reconnect
              </button>
            )}
          </div>
        </div>
      )}

      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Calculator className="h-8 w-8 text-blue-600" />
              <h1 className="ml-2 text-xl font-semibold text-gray-900">
                Auto Lease Analyzer
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {user ? (
                <>
                  <span className="text-sm text-gray-600">
                    {user.email}
                  </span>
                  <button
                    onClick={() => auth.signOut()}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
              {analysis && user && (
                <button
                  onClick={() => {
                    setAnalysis(null);
                    setActiveTab('history');
                  }}
                  className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to History
                </button>
              )}
              
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg sm:text-xl font-semibold flex items-center text-gray-900">
                  <Car className="w-5 h-5 mr-2 text-blue-500" />
                  Lease Details
                </h2>
                {leaseData.msrp && (
                  <button
                    onClick={handleGreenThumb}
                    className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    title="See what good lease terms would look like for this MSRP"
                  >
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Green Thumb
                  </button>
                )}
              </div>

              <FormFields
                leaseData={leaseData}
                validationErrors={validationErrors}
                handleInputChange={handleInputChange}
              />

              <div className="flex justify-between items-center pt-4">
                <button
                  onClick={handleAnalyze}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Analyze Lease
                </button>

                {user && analysis && (
                  <button
                    onClick={handleSaveAnalysis}
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    {saving ? 'Saving...' : 'Save Analysis'}
                  </button>
                )}
              </div>

              {saveError && (
                <div className="mt-4 p-4 bg-red-50 rounded-md">
                  <div className="flex">
                    <AlertTriangle className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Error saving analysis</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{saveError}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div>
            {analysis ? (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h2 className="text-lg sm:text-xl font-semibold mb-6 flex items-center text-gray-900">
                  <FileText className="w-5 h-5 mr-2 text-blue-500" />
                  Analysis Results
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="text-sm text-blue-600 font-medium">Monthly Payment</div>
                    <div className="text-2xl font-bold text-blue-900">
                      ${analysis.monthlyPayment.toFixed(2)}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="text-sm text-blue-600 font-medium">Total Cost</div>
                    <div className="text-2xl font-bold text-blue-900">
                      ${analysis.totalCost.toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="text-sm text-blue-600 font-medium">Effective APR</div>
                    <div className="text-2xl font-bold text-blue-900">
                      {analysis.effectiveAPR.toFixed(2)}%
                    </div>
                  </div>
                </div>

                {/* Overall Assessment */}
                <div className="mb-8 bg-gray-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Overall Assessment</h3>
                    {analysis.findings.filter(f => f.type === 'warning').length <= 1 ? (
                      <ThumbsUp className="w-8 h-8 text-green-500" />
                    ) : (
                      <ThumbsDown className="w-8 h-8 text-red-500" />
                    )}
                  </div>
                  <div className="prose text-gray-600">
                    <p className="mb-4">
                      The total cost of ${analysis.totalCost.toLocaleString()} over {leaseData.term} months includes:
                    </p>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>Monthly payments of ${analysis.monthlyPayment.toFixed(2)} × {leaseData.term} months = ${(analysis.monthlyPayment * parseInt(leaseData.term)).toLocaleString()}</li>
                      <li>Down payment of ${parseFloat(leaseData.downPayment).toLocaleString()}</li>
                      <li>Total fees and add-ons of ${parseFloat(leaseData.fees).toLocaleString()}</li>
                      {parseFloat(leaseData.taxes) > 0 && (
                        <li>Taxes of ${parseFloat(leaseData.taxes).toLocaleString()}</li>
                      )}
                    </ul>
                    <p className="mt-4">
                      This lease has an effective APR of {analysis.effectiveAPR.toFixed(2)}% and a residual value of ${parseFloat(leaseData.residualValue).toLocaleString()} 
                      ({((parseFloat(leaseData.residualValue) / parseFloat(leaseData.msrp)) * 100).toFixed(1)}% of MSRP).
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  {analysis.findings.map((finding, index) => {
                    const Icon = finding.type === 'good' 
                      ? CheckCircle2 
                      : finding.type === 'warning' 
                        ? AlertCircle 
                        : AlertTriangle;
                    
                    const colors = {
                      good: 'bg-green-50 text-green-800 border-green-100',
                      warning: 'bg-red-50 text-red-800 border-red-100',
                      negotiate: 'bg-yellow-50 text-yellow-800 border-yellow-100'
                    };

                    return (
                      <div 
                        key={index}
                        className={`rounded-xl p-4 border ${colors[finding.type]}`}
                      >
                        <div className="flex items-start">
                          <Icon className={`w-5 h-5 mr-3 ${
                            finding.type === 'good' 
                              ? 'text-green-500' 
                              : finding.type === 'warning'
                                ? 'text-red-500'
                                : 'text-yellow-500'
                          }`} />
                          <div>
                            <h4 className="font-medium">{finding.title}</h4>
                            <p className="mt-1 text-sm opacity-90">{finding.message}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : user ? (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h2 className="text-lg sm:text-xl font-semibold mb-6 flex items-center text-gray-900">
                  <History className="w-5 h-5 mr-2 text-blue-500" />
                  Lease History
                </h2>
                <LeaseHistory userId={user.uid} onSelectLease={handleSelectLease} />
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="text-center">
                  <LogIn className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Sign in to view history
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Create an account to save and compare lease analyses
                  </p>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign In
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        isLogin={isLogin}
        setIsLogin={setIsLogin}
      />

      {comparisonLease && (
        <ComparisonModal
          isOpen={showComparisonModal}
          onClose={() => setShowComparisonModal(false)}
          currentLease={{
            data: leaseData,
            analysis: analysis!
          }}
          comparisonLease={comparisonLease}
        />
      )}
    </div>
  );
}

export default App;